import * as R from 'ramda';

import { Progress } from '@atom/mui';
import { BudgetUnitAlignPreview } from '@atom/types/budget';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';

import classes from './budgetAlign.module.css';

interface Props {
  previewData: BudgetUnitAlignPreview;
  selectedItems: Set<string>;
}

const BudgetAlignMain = ({ previewData, selectedItems }: Props) => {
  if (R.isNil(previewData)) {
    return <Progress />;
  }

  const leadingText =
    selectedItems.size === previewData.adjustedItemCount
      ? `You've selected ${previewData.adjustedItemCount} budget items to adjust.
      All selected items will be adjusted proportionally so that the total budget
      matches the authorized budget amount.`
      : `You've selected ${selectedItems.size} budget items to adjust.
      From your selection, ${previewData.adjustedItemCount} budget items will be adjusted proportionally so that
      the total budget matches the authorized budget amount. The rest will be omitted
      because of null or zero amounts.`;

  const adjustmentDirection =
    previewData.adjustmentNeeded <= 0 ? 'decrease' : 'increase';

  return (
    <>
      <p>{leadingText}</p>
      <div className={classes.tallySheet}>
        <div className={classes.tallySheetContainer}>
          <div>
            <div>Authorized budget</div>
            <div>{numberToLocaleString(previewData.authorizedBudget)}</div>
          </div>
          <div>
            <div>Unselected items</div>
            <div>{numberToLocaleString(previewData.omittedItemsTotal)}</div>
          </div>
          <div>
            <div>Authorized remaining for selected items</div>
            <div>{numberToLocaleString(previewData.availableForSelected)}</div>
          </div>
        </div>
        <div className={classes.tallySheetContainer}>
          <div>
            <div>Current total of selected items</div>
            <div>{numberToLocaleString(previewData.selectedTotal)}</div>
          </div>
          <div>
            <div>Adjustment needed</div>
            <div>
              {numberToLocaleString(previewData.adjustmentNeeded)} (
              {previewData.adjustmentPercentage}%)
            </div>
          </div>
        </div>
      </div>
      <div>
        <p>
          {`Every selected budget item will ${adjustmentDirection} by `}
          <span className={classes.tallySummaryValue}>
            {previewData.adjustmentPercentage}%
          </span>
          {`. Selected items will ${adjustmentDirection} by `}
          <span className={classes.tallySummaryValue}>
            {numberToLocaleString(Math.abs(previewData.adjustmentNeeded))}
          </span>
          {' from '}
          <span className={classes.tallySummaryValue}>
            {numberToLocaleString(previewData.selectedTotal)}
          </span>
          {' to '}
          <span className={classes.tallySummaryValue}>
            {numberToLocaleString(previewData.availableForSelected)}
          </span>
          {', bringing the total budget to '}
          <span className={classes.tallySummaryValue}>
            {numberToLocaleString(previewData.authorizedBudget)}
          </span>
          .
        </p>
      </div>
    </>
  );
};

export default BudgetAlignMain;
